<template>
  <div
    ref="modalEl"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="barcodeScannerTitle"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="barcodeScannerTitle">Barcode scannen</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Schließen"
          ></button>
        </div>
        <div class="modal-body">
          <div class="scanner-stage">
            <video ref="videoEl" class="scanner-video" muted playsinline autoplay></video>
            <div class="scanner-reticle" aria-hidden="true"></div>
            <button
              v-if="torchAvailable"
              type="button"
              class="scanner-torch"
              :class="{ on: torchOn }"
              :aria-pressed="torchOn"
              title="Taschenlampe"
              @click="toggleTorch"
            >🔦</button>
          </div>

          <div v-if="devices.length > 1" class="scanner-camera-select">
            <label for="scannerCamera" class="form-label">Kamera</label>
            <select
              id="scannerCamera"
              class="form-select form-select-sm"
              :value="selectedDeviceId"
              @change="onCameraChange"
            >
              <option
                v-for="(d, i) in devices"
                :key="d.deviceId"
                :value="d.deviceId"
              >{{ d.label || `Kamera ${i + 1}` }}</option>
            </select>
          </div>

          <p v-if="error" class="scanner-error">{{ error }}</p>
          <p v-else class="scanner-hint">
            Barcode (IMEI, Vertrags- oder Schadensnummer) vor die Kamera halten.
          </p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { Modal } from 'bootstrap';
import {
  BrowserCodeReader,
  BrowserMultiFormatReader,
  type IScannerControls,
} from '@zxing/browser';

/**
 * Kamera-Barcodescanner als Bootstrap-Modal. `open()` startet die Kamera
 * (bevorzugt die Haupt-Rückkamera) und löst `scanned` aus, sobald ein Code
 * erkannt wurde. Auf Handys mit mehreren Linsen kann die Kamera per Dropdown
 * gewechselt werden; die Wahl wird gemerkt.
 */
const emit = defineEmits<{
  (e: 'scanned', value: string): void;
}>();

const STORAGE_KEY = 'r2.barcode.cameraId';

const modalEl = useTemplateRef<HTMLDivElement>('modalEl');
const videoEl = useTemplateRef<HTMLVideoElement>('videoEl');
const error   = ref<string | null>(null);

const devices          = ref<MediaDeviceInfo[]>([]);
const selectedDeviceId = ref<string>('');
const torchAvailable   = ref(false);
const torchOn          = ref(false);

let modal:    Modal | null = null;
let reader:   BrowserMultiFormatReader | null = null;
let controls: IScannerControls | null = null;

onMounted(() => {
  if (modalEl.value) {
    modal = new Modal(modalEl.value);
    modalEl.value.addEventListener('shown.bs.modal', startScan);
    modalEl.value.addEventListener('hidden.bs.modal', stopScan);
  }
});

onBeforeUnmount(() => {
  modalEl.value?.removeEventListener('shown.bs.modal', startScan);
  modalEl.value?.removeEventListener('hidden.bs.modal', stopScan);
  stopScan();
  modal?.dispose();
  modal = null;
});

function open(): void {
  error.value = null;
  modal?.show();
}

async function startScan(): Promise<void> {
  if (!videoEl.value) return;

  if (!navigator.mediaDevices?.getUserMedia) {
    error.value = 'Diese Umgebung unterstützt keinen Kamerazugriff. Bitte den Code manuell eingeben.';
    return;
  }

  error.value = null;
  reader = new BrowserMultiFormatReader();

  try {
    const saved = selectedDeviceId.value || localStorage.getItem(STORAGE_KEY) || '';
    if (saved) {
      await runDevice(saved);
    } else {
      // Erststart: über facingMode schnell Vorschau + Berechtigung holen.
      controls = await reader.decodeFromConstraints(
        { video: { facingMode: { ideal: 'environment' } } },
        videoEl.value,
        onResult,
      );
    }

    // Geräteliste füllen (Labels erst nach erteilter Berechtigung verfügbar).
    devices.value = await BrowserCodeReader.listVideoInputDevices();

    // Ohne gemerkte Wahl: beste Rückkamera bestimmen und ggf. darauf wechseln.
    if (!selectedDeviceId.value) {
      const best = pickBackCamera(devices.value);
      if (best?.deviceId) {
        await runDevice(best.deviceId);
      }
    }

    refreshTorchAvailability();
  } catch (e) {
    handleStartError(e);
  }
}

/** Startet (bzw. wechselt auf) eine konkrete Kamera per deviceId. */
async function runDevice(deviceId: string): Promise<void> {
  if (!reader || !videoEl.value) return;
  controls?.stop();
  controls = null;
  torchOn.value = false;
  controls = await reader.decodeFromVideoDevice(deviceId || undefined, videoEl.value, onResult);
  selectedDeviceId.value = deviceId;
  if (deviceId) localStorage.setItem(STORAGE_KEY, deviceId);
  refreshTorchAvailability();
}

async function onCameraChange(ev: Event): Promise<void> {
  const id = (ev.target as HTMLSelectElement).value;
  try {
    await runDevice(id);
  } catch (e) {
    handleStartError(e);
  }
}

/**
 * Wählt die wahrscheinlich beste Rückkamera: bevorzugt eine als „back/rück/rear"
 * gekennzeichnete Hauptlinse und meidet Ultraweitwinkel-/Tele-/Tiefenlinsen,
 * die schlecht nah fokussieren.
 */
function pickBackCamera(list: MediaDeviceInfo[]): MediaDeviceInfo | undefined {
  if (list.length === 0) return undefined;
  const backs = list.filter(d => /back|rear|rück|environment/i.test(d.label));
  const pool  = backs.length ? backs : list;
  return (
    pool.find(d => /\bback camera\b/i.test(d.label)) ??
    pool.find(d => !/(ultra|wide|tele|depth|zoom)/i.test(d.label)) ??
    pool[0]
  );
}

function refreshTorchAvailability(): void {
  torchAvailable.value = typeof controls?.switchTorch === 'function';
}

async function toggleTorch(): Promise<void> {
  if (!controls?.switchTorch) return;
  try {
    await controls.switchTorch(!torchOn.value);
    torchOn.value = !torchOn.value;
  } catch {
    torchAvailable.value = false;
  }
}

function onResult(result: unknown): void {
  // DecodeContinuouslyCallback liefert bei Treffer ein Result mit getText().
  const r = result as { getText?: () => string } | null;
  const text = r?.getText?.().trim();
  if (text) onDecoded(text);
}

function handleStartError(e: unknown): void {
  const name = (e as Error)?.name;
  error.value = name === 'NotAllowedError'
    ? 'Kamerazugriff wurde verweigert. Bitte in den Browser-Einstellungen erlauben.'
    : `Kamera konnte nicht gestartet werden: ${(e as Error)?.message ?? 'Unbekannter Fehler'}`;
}

function onDecoded(text: string): void {
  // erst stoppen, dann schließen — verhindert Mehrfach-Treffer
  stopScan();
  emit('scanned', text);
  modal?.hide();
}

function stopScan(): void {
  controls?.stop();
  controls = null;
  reader   = null;
  torchOn.value = false;
}

defineExpose({ open });
</script>

<style scoped>
.scanner-stage {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  background: #000;
  border-radius: 10px;
  overflow: hidden;
}

.scanner-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.scanner-reticle {
  position: absolute;
  inset: 18% 12%;
  border: 2px solid rgba(255, 255, 255, 0.85);
  border-radius: 12px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.25);
  pointer-events: none;
}

.scanner-torch {
  position: absolute;
  right: 10px;
  bottom: 10px;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
}

.scanner-torch.on {
  background: var(--accent, #f7a600);
}

.scanner-camera-select {
  margin-top: 0.75rem;
}

.scanner-camera-select .form-label {
  margin-bottom: 0.25rem;
  font-size: 0.85rem;
  color: #6c757d;
}

.scanner-hint {
  margin: 0.75rem 0 0;
  color: #6c757d;
  font-size: 0.9rem;
  text-align: center;
}

.scanner-error {
  margin: 0.75rem 0 0;
  color: #b91c1c;
  font-size: 0.9rem;
  text-align: center;
}
</style>
